import * as THREE from 'three';
import { BLACK, basic, cloth, emis, std } from '../materials.ts';
import { M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * El Uro Viejo (El Cuerno 3, docs/SAGAS.md): the bull of ash that hunts the clan's herd up on the titan's tail. A
 * wild aurochs grown huge on the dream: a mountain of a hump over the shoulders, a deep chest and a low, heavy head
 * carried forward, great pale horns sweeping out, forward and up, a shaggy black mane over the neck and hump, lean
 * hindquarters and a long tufted tail. The ash of its hide is split by seams of ember (restrained: it is ash, not
 * lava); its eyes and nostrils smoulder, and a thin smoke rises off its back. Opaque shared materials; the ember is
 * its one self-lit material and the smoke its one translucent one, plus a few glow sprites.
 */

const UP = V(0, 1, 0);

/** Hip, knee and hoof of each leg (the right side, +X; the left mirrors x): front, then rear. */
const FORE = { hip: V(0.44, 1.35, 0.78), knee: V(0.46, 0.72, 0.84), foot: V(0.46, 0.1, 0.8) };
const HIND = { hip: V(0.42, 1.38, -1.0), knee: V(0.45, 0.72, -1.16), foot: V(0.45, 0.1, -1.02) };

/** A tapering tube along a curve (the horns, the tail), its tip pointed. */
const taper = (pts: THREE.Vector3[], r0: number, r1: number, seg = 8, rad = 7): THREE.BufferGeometry => {
  const curve = new THREE.CatmullRomCurve3(pts);
  const fr = curve.computeFrenetFrames(seg, false);
  const pos: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= seg; i++) {
    const k = i / seg;
    const c = curve.getPointAt(k);
    const r = r0 + (r1 - r0) * k;
    const n = fr.normals[i] as THREE.Vector3, b = fr.binormals[i] as THREE.Vector3;
    for (let j = 0; j < rad; j++) {
      const a = (j / rad) * Math.PI * 2;
      const p = c.clone().addScaledVector(n, Math.cos(a) * r).addScaledVector(b, Math.sin(a) * r);
      pos.push(p.x, p.y, p.z);
    }
  }
  const tip = curve.getPointAt(1).addScaledVector(fr.tangents[seg] as THREE.Vector3, r1 * 2);
  pos.push(tip.x, tip.y, tip.z);
  const ti = (seg + 1) * rad;
  for (let i = 0; i < seg; i++) {
    for (let j = 0; j < rad; j++) {
      const a = i * rad + j, b2 = i * rad + ((j + 1) % rad), c = a + rad, d = b2 + rad;
      idx.push(a, c, b2, b2, c, d);
    }
  }
  for (let j = 0; j < rad; j++) idx.push(seg * rad + j, ti, seg * rad + ((j + 1) % rad));
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
};

/**
 * Bones: `body` pivots at the hind hips (it drops its head and drives forward over them) and carries the fore legs,
 * the head and the tail; the hind legs hang from the model group; each leg bends at the knee.
 */
export const uro = (o?: BaseOpts): Model => {
  const c = begin(o, 1.3, 61);
  const { g, R, rng } = c;
  const ash = std('#3b3b42', { roughness: 0.92, flatShading: true, bumpScale: 0.06 });
  const ashD = std('#222128', { roughness: 0.95, flatShading: true, bumpScale: 0.07 });
  const mane = cloth('#151316', { side: THREE.DoubleSide, flatShading: true, bumpScale: 0.05 });
  const horn = std('#aeb3ba', { roughness: 0.55, flatShading: true });
  const hoof = std('#141317', { roughness: 0.6 });
  const ember = emis('#ff8a6e', '#ff4f3a', 1.25);

  const bodyJ = joint(g, V(0, 1.38, -1.0), 'body');
  const bd = bodyJ.abs;
  const headJ = joint(bd, V(0, 1.85, 1.15), 'head');
  const hd = headJ.abs;
  const tailJ = joint(bd, V(0, 1.72, -1.42), 'tail');
  const foreJ = [-1, 1].map((k) => joint(bd, V(k * FORE.hip.x, FORE.hip.y, FORE.hip.z), k < 0 ? 'foreL' : 'foreR'));
  const fore2J = [-1, 1].map((k, i) => joint(foreJ[i]!.abs, V(k * FORE.knee.x, FORE.knee.y, FORE.knee.z), k < 0 ? 'foreL2' : 'foreR2'));
  const hindJ = [-1, 1].map((k) => joint(g, V(k * HIND.hip.x, HIND.hip.y, HIND.hip.z), k < 0 ? 'hindL' : 'hindR'));
  const hind2J = [-1, 1].map((k, i) => joint(hindJ[i]!.abs, V(k * HIND.knee.x, HIND.knee.y, HIND.knee.z), k < 0 ? 'hindL2' : 'hindR2'));

  /** A lump: a squashed sphere. */
  const lump = (parent: THREE.Object3D, mat: THREE.Material, r: number, x: number, y: number, z: number, sx = 1, sy = 1, sz = 1, seg = 9): THREE.Mesh => {
    const m = M(new THREE.SphereGeometry(r, seg, Math.max(5, seg - 3)), mat, parent);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    return m;
  };
  /** A thin seam of ember over the ash: jittered points joined by a glowing tube. */
  const crack = (parent: THREE.Object3D, pts: THREE.Vector3[], r = 0.016): void => {
    const jit: THREE.Vector3[] = [];
    pts.forEach((p, i) => {
      const q = pts[i + 1];
      jit.push(p.clone().add(V(R() * 0.03, R() * 0.03, R() * 0.03)));
      if (q) jit.push(p.clone().lerp(q, 0.5).add(V(R() * 0.05, R() * 0.05, R() * 0.05)));
    });
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(jit, false, 'catmullrom', 0.05), jit.length, r, 3), ember, parent);
  };
  /** A hank of the mane: a strand hanging from `top` to a ragged point, leaning out by −`out`. */
  const hank = (parent: THREE.Object3D, top: THREE.Vector3, len: number, w: number, yaw: number, out = 0.15): void => {
    const gm = new THREE.PlaneGeometry(w, len, 1, 3);
    gm.translate(0, -len / 2, 0);
    const p = gm.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      const d = -p.getY(i) / len;
      p.setX(i, p.getX(i) * (1 - 0.85 * d) + R() * 0.04 * d);
      p.setZ(i, 0.05 * Math.sin(d * 3 + i));
    }
    gm.computeVertexNormals();
    const m = M(gm, mane, parent);
    m.position.copy(top);
    m.rotation.set(0, yaw, 0);
    m.rotateX(out);
  };

  // Legs: long and sinewy for its bulk, thick at the forearm and the gaskin, on split black hooves.
  for (const [k, i] of [[-1, 0], [1, 1]] as const) {
    for (const [D, up, lo, fore] of [[FORE, foreJ[i]!.abs, fore2J[i]!.abs, true], [HIND, hindJ[i]!.abs, hind2J[i]!.abs, false]] as const) {
      const hip = V(k * D.hip.x, D.hip.y, D.hip.z), knee = V(k * D.knee.x, D.knee.y, D.knee.z), foot = V(k * D.foot.x, D.foot.y, D.foot.z);
      limb(up, hip.clone().add(V(0, 0.12, 0)), knee, fore ? 0.27 : 0.3, 0.15, ash, 8);
      lump(up, ash, 0.3, hip.x, hip.y - 0.12, hip.z + (fore ? 0.02 : -0.04), 0.9, 1.35, 1.1, 8);
      lump(lo, ashD, 0.14, knee.x, knee.y, knee.z, 1, 1, 1, 6);
      limb(lo, knee, foot.clone().add(V(0, 0.08, 0)), 0.13, 0.11, ash, 7);
      // The fetlock's tuft and the split hoof.
      lump(lo, mane, 0.13, foot.x, 0.24, foot.z - 0.02, 1, 0.9, 1, 6);
      for (const s of [-1, 1]) {
        const h = M(new THREE.CylinderGeometry(0.06, 0.085, 0.16, 6), hoof, lo);
        h.position.set(foot.x + s * 0.055, 0.08, foot.z + 0.03);
        h.rotation.x = 0.12;
      }
      crack(lo, [V(k * (D.knee.x + 0.1), 0.66, D.knee.z + 0.06), V(k * (D.knee.x + 0.11), 0.45, D.knee.z + 0.02), V(k * (D.knee.x + 0.1), 0.28, D.knee.z + 0.05)], 0.01);
    }
  }

  // The body: a deep chest under a mountain of a hump, the back falling away to lean hindquarters.
  lump(bd, ash, 1, 0, 1.55, -0.15, 0.72, 0.62, 1.25, 12);
  lump(bd, ash, 0.75, 0, 1.95, 0.5, 0.95, 0.95, 1.05, 11);
  lump(bd, ash, 0.6, 0, 2.35, 0.32, 0.85, 0.85, 1.05, 10);
  lump(bd, ash, 0.6, 0, 1.4, 0.75, 1.0, 1.05, 0.85, 9);
  lump(bd, ash, 0.55, 0, 1.6, -1.0, 0.95, 0.9, 0.85, 9);
  lump(bd, ashD, 0.4, 0, 1.12, -0.25, 0.95, 0.5, 1.6, 8);
  // The dewlap: a heavy fold hanging under the throat.
  lump(bd, ashD, 0.3, 0, 1.2, 1.1, 0.7, 1.2, 0.8, 7);
  // Ember seams over the flanks and the hump, where the ash has split; the heart smoulders behind the shoulder.
  for (const k of [-1, 1]) {
    crack(bd, [V(k * 0.55, 2.55, 0.45), V(k * 0.72, 2.2, 0.3), V(k * 0.74, 1.85, 0.05), V(k * 0.7, 1.55, -0.35)]);
    crack(bd, [V(k * 0.68, 1.85, -0.6), V(k * 0.7, 1.6, -0.85), V(k * 0.62, 1.35, -1.1)], 0.013);
    crack(bd, [V(k * 0.62, 1.4, 0.8), V(k * 0.6, 1.2, 0.55)], 0.012);
    glowSprite(bd, '#ff4f3a', 0.5, V(k * 0.74, 1.8, 0.1), 0.22);
  }
  crack(bd, [V(0.08, 2.92, 0.5), V(-0.05, 2.85, 0.2), V(0.06, 2.6, -0.15), V(-0.03, 2.2, -0.55)], 0.014);

  // The mane: black hanks down the neck and over the hump, hanging longest at the shoulder; a beard at the dewlap.
  // (They hug the hump and fall down the shoulder: hung any farther out they read as spines from the table camera.)
  for (const k of [-1, 1]) {
    for (let n = 0; n < 8; n++) {
      const z = 1.05 - n * 0.2;
      const hump = Math.max(0, 1 - Math.abs(z - 0.45) / 0.8);
      hank(bd, V(k * (0.42 + 0.3 * hump), 2.2 + 0.42 * hump, z + R() * 0.05), 0.3 + 0.3 * hump + rng() * 0.1, 0.17, k * Math.PI / 2 + R() * 0.25, 0.2);
    }
  }
  // A shaggy cap over the top of the hump: dark lumps, not blades.
  for (let n = 0; n < 5; n++) lump(bd, mane, 0.26, R() * 0.12, 2.82 - Math.abs(n - 1.5) * 0.09, 0.75 - n * 0.24, 1.25, 0.5, 1.0, 6);
  for (let n = 0; n < 3; n++) hank(bd, V((n - 1) * 0.14, 1.05, 1.22), 0.5 + rng() * 0.15, 0.2, (n - 1) * 0.4, 0.1);

  // The tail: long and thin, a tuft of black hair at its end.
  {
    const tl = tailJ.abs;
    M(taper([V(0, 1.72, -1.42), V(0, 1.45, -1.56), V(0, 1.05, -1.6), V(0, 0.75, -1.58)], 0.06, 0.03, 6, 5), ashD, tl);
    const tuft = M(new THREE.ConeGeometry(0.1, 0.36, 6), mane, tl);
    tuft.position.set(0, 0.6, -1.58);
    tuft.rotation.x = Math.PI;
  }

  // Head: a long, heavy skull carried low, a broad curly brow, the muzzle dark; ember eyes and smouldering nostrils.
  const skull = lump(hd, ash, 0.42, 0, 1.8, 1.45, 0.82, 0.85, 1.15, 10);
  skull.rotation.x = 0.5;
  lump(hd, ash, 0.3, 0, 1.52, 1.82, 0.85, 0.8, 1.1, 9);
  const muzzle = lump(hd, ashD, 0.24, 0, 1.3, 2.02, 1.05, 0.8, 0.95, 8);
  muzzle.rotation.x = 0.3;
  // The brow: a mat of curled black hair between the horns.
  for (let n = 0; n < 5; n++) lump(hd, mane, 0.11, (n - 2) * 0.1, 2.05 + Math.abs(n - 2) * -0.02, 1.52 + (n % 2) * 0.04, 1, 0.8, 1, 5);
  for (const k of [-1, 1]) {
    // Eyes deep under the brow; nostrils at the end of the muzzle (they puff when it blows).
    lump(hd, BLACK, 0.06, k * 0.27, 1.8, 1.72, 1, 0.65, 0.7, 6);
    lump(hd, ember, 0.03, k * 0.29, 1.8, 1.76, 1.2, 0.7, 0.7, 5);
    glowSprite(hd, '#ff4f3a', 0.18, V(k * 0.3, 1.8, 1.78), 0.8);
    lump(hd, ember, 0.035, k * 0.08, 1.25, 2.24, 1.1, 0.7, 0.5, 5);
    // Ears, out sideways under the horns.
    const ear = lump(hd, ashD, 0.16, k * 0.44, 1.85, 1.38, 1.1, 0.4, 0.75, 6);
    ear.rotation.z = k * -0.4;
    // The horns: thick at the root, out and back a little, then sweeping forward and up; charred tips.
    lump(hd, ash, 0.13, k * 0.3, 2.02, 1.4, 1.2, 1, 1, 6);
    const pts = [V(0.3, 2.02, 1.4), V(0.68, 2.1, 1.3), V(1.0, 2.2, 1.46), V(1.12, 2.4, 1.82), V(0.98, 2.62, 2.12), V(0.78, 2.78, 2.26)].map((v) => v.setX(v.x * k));
    M(taper(pts, 0.13, 0.03, 12, 7), horn, hd);
    const tip = M(new THREE.ConeGeometry(0.032, 0.15, 6), BLACK, hd);
    tip.position.set(k * 0.74, 2.82, 2.28);
    tip.quaternion.setFromUnitVectors(UP, V(-k * 0.45, 0.75, 0.45).normalize());
    // Rings worn into the horn near the root.
    for (const u of [0.12, 0.2]) {
      const ring = M(new THREE.TorusGeometry(0.115, 0.016, 3, 8), ashD, hd);
      const cv = new THREE.CatmullRomCurve3(pts);
      ring.position.copy(cv.getPointAt(u));
      ring.quaternion.setFromUnitVectors(V(0, 0, 1), cv.getTangentAt(u));
    }
  }
  crack(hd, [V(-0.1, 2.0, 1.55), V(0.03, 1.75, 1.78), V(-0.04, 1.5, 1.95)], 0.013);

  // A thin smoke off the hump, and one off the haunch.
  const ribs: Ribbon[] = [];
  const wm = basic('#3a3440', 0.4);
  const SMOKE: [number, number, number, number][] = [[0.0, 2.95, 0.35, 0.9], [0.25, 2.05, -0.9, 0.55]];
  for (const [x, y, z, h] of SMOKE) ribs.push(new Ribbon(bd, wisp(x, z, h, 6).map((p) => p.add(V(0, y, 0))), 0.04, wm, rng, 0.07));
  const heart = glowSprite(bd, '#ff4f3a', 1.1, V(0, 1.6, 0.85), 0.16, true);

  // Only the boil here: every move is the clips' (anim/creatures/uro.ts).
  const t = (n: number): void => {
    for (const r of ribs) r.step();
    heart.material.opacity = 0.13 + ((n * 3) % 7) / 60;
  };
  t(0);
  return done(c, 2.95, t, undefined, bonesOf(c, {
    body: bodyJ.p, head: headJ.p, tail: tailJ.p,
    foreL: foreJ[0]!.p, foreL2: fore2J[0]!.p, foreR: foreJ[1]!.p, foreR2: fore2J[1]!.p,
    hindL: hindJ[0]!.p, hindL2: hind2J[0]!.p, hindR: hindJ[1]!.p, hindR2: hind2J[1]!.p,
  }));
};

/** Where its nostrils are on the model (before scaling), for the steam it blows. */
export const URO_NOSE = V(0, 1.25, 2.26);
